import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChalkboardTeacher, faTrash, faCheckCircle, faExclamationTriangle,
  faPlus, faSyncAlt, faUserGraduate, faSpinner, faSync
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
// Contexte optionnel pour le refresh global
import { FormationsContext } from '../layout/CunstomFormations'; // ajustez le chemin

const API_URL = `${API_CONFIG2.apiUrl}formation/api_assigneformation.php`;

// ---- Dexie ----
const db = new Dexie('AssignationCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h
const CACHE_KEY = 'assignations_data';

// ---- Map pour dédoublonner les requêtes en cours ----
const pendingFetches = new Map();

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ (inchangé)
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const AssignationFormation = ({ userId }) => {
  const context = useContext(FormationsContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [formations, setFormations] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [assignations, setAssignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [formData, setFormData] = useState({ formation_id: '', formateur_id: '' });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  // Réf. pour annulation et déduplication
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // CACHE HELPERS
  // ============================================================
  const loadFromCache = async () => {
    try {
      const cached = await db.cache.get(CACHE_KEY);
      if (cached && cached.timestamp && (Date.now() - cached.timestamp) < CACHE_TTL) {
        return cached.data;
      }
      return null;
    } catch { return null; }
  };

  const saveToCache = async (data) => {
    try {
      await db.cache.put({ key: CACHE_KEY, data, timestamp: Date.now() });
    } catch { /* ignore */ }
  };

  const loadStaleCache = async () => {
    try {
      const cached = await db.cache.get(CACHE_KEY);
      return cached ? cached.data : null;
    } catch { return null; }
  };

  const invalidateCache = async () => {
    try {
      await db.cache.delete(CACHE_KEY);
    } catch { /* ignore */ }
  };

  // ============================================================
  // CHARGEMENT DES DONNÉES (avec dédoublonnage, annulation, cache)
  // ============================================================
  const fetchData = useCallback(async (force = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Clé de dédoublonnage
    const requestKey = force ? `${CACHE_KEY}_force` : CACHE_KEY;

    // 1. Dédoublonnage : si une requête identique est en cours, on attend sa résolution
    if (pendingFetches.has(requestKey) && !force) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setFormations(result.formations || []);
          setFormateurs(result.formateurs || []);
          setAssignations(result.assignations || []);
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // 2. Limiter les tentatives
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        setToast({ show: true, message: 'Erreur : trop de tentatives.', variant: 'danger' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
      }
      return;
    }

    // 3. Définir l'état de chargement
    if (force) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    // 4. Vérifier le cache (sauf force)
    if (!force) {
      const cached = await loadFromCache();
      if (cached && isMounted.current) {
        setFormations(cached.formations || []);
        setFormateurs(cached.formateurs || []);
        setAssignations(cached.assignations || []);
        if (setSource) setSource('cache');
        setLoading(false);
        setRefreshing(false);
        return;
      }
    }

    // 5. Requête réseau
    const fetchPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}?action=fetch_all`, {
          signal: controller.signal
        });

        // Gestion 429
        if (response.status === 429) {
          // Utiliser le cache périmé
          const stale = await loadStaleCache();
          if (stale && isMounted.current) {
            setFormations(stale.formations || []);
            setFormateurs(stale.formateurs || []);
            setAssignations(stale.assignations || []);
            if (setSource) setSource('cache (périmé)');
            setToast({
              show: true,
              message: '429 – Utilisation du cache (peut être obsolète).',
              variant: 'warning'
            });
            setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
            setLoading(false);
            setRefreshing(false);
            return;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchData(force, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await loadStaleCache();
          if (stale && isMounted.current) {
            setFormations(stale.formations || []);
            setFormateurs(stale.formateurs || []);
            setAssignations(stale.assignations || []);
            if (setSource) setSource('cache (périmé)');
            setToast({
              show: true,
              message: `Erreur réseau – Utilisation du cache`,
              variant: 'warning'
            });
            setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
            setLoading(false);
            setRefreshing(false);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const payload = {
            formations: data.formations || [],
            formateurs: data.formateurs || [],
            assignations: data.assignations || []
          };
          if (isMounted.current) {
            setFormations(payload.formations);
            setFormateurs(payload.formateurs);
            setAssignations(payload.assignations);
            if (setSource) setSource(force ? 'live (forcé)' : 'live');
            await saveToCache(payload);
            setLoading(false);
            setRefreshing(false);
          }
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Dernier recours : cache périmé
        const stale = await loadStaleCache();
        if (stale && isMounted.current) {
          setFormations(stale.formations || []);
          setFormateurs(stale.formateurs || []);
          setAssignations(stale.assignations || []);
          if (setSource) setSource('cache (périmé)');
          setToast({
            show: true,
            message: `Erreur – Utilisation du cache : ${err.message}`,
            variant: 'warning'
          });
          setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
        setLoading(false);
        setRefreshing(false);
      }
    })();

    // Stocker la promesse pour dédoublonner
    pendingFetches.set(requestKey, fetchPromise);
    try {
      await fetchPromise;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [setSource]);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchData(true);
    }
  }, [refreshKey, fetchData]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchData(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pendingFetches.clear(); // nettoyer la Map au démontage
    };
  }, [fetchData]);

  // ============================================================
  // GESTIONNAIRES DE FORMULAIRE (inchangé)
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.formation_id || !formData.formateur_id) {
      setToast({ show: true, message: 'Veuillez sélectionner une formation et un formateur', variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        formation_id: parseInt(formData.formation_id),
        formateur_id: parseInt(formData.formateur_id),
        userId: userId || null
      };
      const data = await fetchJSON(`${API_URL}?action=create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setFormData({ formation_id: '', formateur_id: '' });
        await invalidateCache();
        fetchData(true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('create error:', err);
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette assignation ?')) return;

    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&id=${id}&userId=${userId || ''}`, {
        method: 'DELETE'
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        await invalidateCache();
        fetchData(true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('delete error:', err);
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  // ============================================================
  // RENDU (identique, avec ajout d'un bouton "Forcer" optionnel)
  // ============================================================
  if (loading && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des données...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* styles inchangés */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }
        .assignation-wrapper { padding: 30px; background: var(--bg-gradient); min-height: 100vh; color: var(--text-primary); font-family: 'Inter', sans-serif; }
        .glass-card { background: var(--card-bg); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border: 1px solid var(--card-border); border-radius: 24px; padding: 1.8rem; box-shadow: var(--shadow); transition: all 0.2s ease; margin-bottom: 24px; }
        .glass-card:hover { border-color: rgba(59, 130, 246, 0.25); box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05); }
        .monetra-input { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; color: var(--text-primary); padding: 12px; width: 100%; transition: 0.3s; }
        .monetra-input:focus { border-color: var(--accent-blue); outline: none; background: rgba(255,255,255,0.06); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06); }
        .monetra-input option { background: #0b1428; color: var(--text-primary); }
        .monetra-btn { padding: 10px 24px; border-radius: 30px; background: var(--accent-blue); color: #fff; border: none; font-weight: 600; transition: all 0.25s ease; box-shadow: 0 6px 20px var(--accent-blue-glow); display: inline-flex; align-items: center; gap: 8px; justify-content: center; }
        .monetra-btn:hover:not(:disabled) { background: var(--accent-dark-blue); transform: translateY(-2px); box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4); }
        .monetra-btn:disabled { background: rgba(255,255,255,0.06); color: var(--text-muted); box-shadow: none; cursor: not-allowed; }
        .monetra-btn.danger { background: rgba(239, 68, 68, 0.12); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.2); box-shadow: none; }
        .monetra-btn.danger:hover:not(:disabled) { background: rgba(239, 68, 68, 0.25); transform: translateY(-2px); }
        .monetra-btn.icon-only { padding: 8px 12px; font-size: 1rem; }
        .monetra-btn.outline-secondary { background: transparent; border: 1px solid rgba(255,255,255,0.06); color: var(--text-secondary); box-shadow: none; }
        .monetra-btn.outline-secondary:hover { background: rgba(255,255,255,0.04); }
        .table-glass { width: 100%; border-collapse: collapse; }
        .table-glass th { padding: 12px 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 1px; color: var(--accent-blue); border-bottom: 1px solid rgba(255,255,255,0.04); }
        .table-glass td { padding: 12px 8px; border-bottom: 1px solid rgba(255,255,255,0.02); }
        .table-glass tr { transition: 0.2s; }
        .table-glass tr:hover { background: rgba(255,255,255,0.03); }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .mb-4 { margin-bottom: 1.5rem; }
        .mb-3 { margin-bottom: 1rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-center { text-align: center; }
        .mt-2 { margin-top: 0.5rem; }
        .w-100 { width: 100%; }
        .row { display: flex; flex-wrap: wrap; }
        .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 12px; }
        .col-12 { flex: 0 0 100%; max-width: 100%; padding: 0 12px; }
        .g-3 { margin: 0 -12px; }
        .table-responsive { overflow-x: auto; }
        .toast-custom { background: rgba(10, 20, 45, 0.8) !important; backdrop-filter: blur(24px) !important; -webkit-backdrop-filter: blur(24px) !important; border: 1px solid rgba(59, 130, 246, 0.2) !important; border-radius: 16px !important; color: var(--text-primary) !important; box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important; }
        .btn-close-custom { background: transparent; border: none; color: var(--text-primary); opacity: 0.6; font-size: 1.2rem; cursor: pointer; }
        .badge-source {
          font-size: 0.7rem;
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(59,130,246,0.12);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.12);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.12);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }
        @media (max-width: 768px) {
          .assignation-wrapper { padding: 16px; }
          .glass-card { padding: 1.2rem; }
          .col-md-6 { flex: 0 0 100%; max-width: 100%; }
        }
        .d-flex { display: flex; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .flex-wrap { flex-wrap: wrap; }
        .gap-3 { gap: 1rem; }
      `}</style>

      <div className="assignation-wrapper">
        {/* En-tête avec badge source et bouton de rafraîchissement */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <h2 className="fw-bold mb-0" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FontAwesomeIcon icon={faChalkboardTeacher} style={{ color: 'var(--accent-blue)' }} />
            Assigner une formation à un formateur
          </h2>
          <div className="d-flex align-items-center gap-3">
            {/* Affichage de la source des données */}
            <span className={`badge-source ${window.source === 'live' ? 'live' : window.source === 'cache (périmé)' ? 'warning' : ''}`}>
              {window.source === 'cache' ? '📦 Cache' :
               window.source === 'cache (périmé)' ? '⚠️ Cache périmé' :
               window.source === 'cache (partagé)' ? '🔄 Partage' :
               window.source === 'live' ? '🔄 Live' :
               window.source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn outline-secondary"
              onClick={() => fetchData(true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSync} spin={loading || refreshing} /> Rafraîchir
            </button>
          </div>
        </div>

        {/* Formulaire d'assignation */}
        <div className="glass-card">
          <Form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="text-secondary small">Formation</Form.Label>
                  <Form.Select
                    name="formation_id"
                    value={formData.formation_id}
                    onChange={handleChange}
                    className="monetra-input"
                    required
                    disabled={submitting}
                  >
                    <option className="bg-dark" value="">-- Sélectionner --</option>
                    {formations.map(f => (
                      <option className="bg-dark" key={f.id} value={f.id}>{f.titre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="text-secondary small">Formateur (service Formation)</Form.Label>
                  <Form.Select
                    name="formateur_id"
                    value={formData.formateur_id}
                    onChange={handleChange}
                    className="monetra-input"
                    required
                    disabled={submitting}
                  >
                    <option className="bg-dark" value="">-- Sélectionner --</option>
                    {formateurs.map(e => (
                      <option className="bg-dark" key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-12">
                <Button type="submit" className="monetra-btn w-100" disabled={submitting}>
                  {submitting ? (
                    <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> En cours...</>
                  ) : (
                    <><FontAwesomeIcon icon={faPlus} className="me-2" /> Assigner</>
                  )}
                </Button>
              </div>
            </div>
          </Form>
        </div>

        {/* Liste des assignations */}
        <div className="glass-card">
          <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)' }}>
            <FontAwesomeIcon icon={faUserGraduate} className="me-2" />
            Assignations existantes
          </h5>
          {assignations.length === 0 ? (
            <p className="text-secondary">Aucune assignation pour le moment.</p>
          ) : (
            <div className="table-responsive">
              <table className="table-glass">
                <thead>
                  <tr>
                    <th>Formation</th>
                    <th>Formateur</th>
                    <th>Date d'assignation</th>
                    <th style={{ width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignations.map(a => (
                    <tr key={a.id}>
                      <td><span className="text-secondary">{a.formation_titre || 'Inconnue'}</span></td>
                      <td><span className="text-secondary">{a.formateur_prenom} {a.formateur_nom}</span></td>
                      <td><span className="text-secondary">{new Date(a.date_assignation).toLocaleString()}</span></td>
                      <td>
                        <button
                          className="monetra-btn danger icon-only"
                          onClick={() => handleDelete(a.id)}
                          disabled={deleting[a.id]}
                        >
                          {deleting[a.id] ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faTrash} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Toast de notification */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, maxWidth: '440px', width: '100%' }}>
          <div className={`toast-custom p-3 ${toast.variant}`}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span className="flex-grow-1">{toast.message}</span>
              <button
                className="btn-close-custom"
                onClick={() => setToast({ ...toast, show: false })}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignationFormation;